import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Checkbox, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { getPagedRunEventKeys } from '@/services/run-event-key.service';
import { RunEventKey, RunEventKeyPaginationFilterRequest } from '@/models/run-event-key.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import RunEventKeyDrawer from './run-event-key-drawer';
import FetchingSelect from '@/components/FetchingSelect';
import { getCitiesLookup } from '@/services/city.service';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const RunEventKeyList = () => {
  const [filterReq, setFilterReq] = useState<RunEventKeyPaginationFilterRequest>(new RunEventKeyPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunEventKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [idToEdit, setIdToEdit] = useState<number|undefined>(undefined);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);
  
  const fetchData = async (req: RunEventKeyPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<RunEventKey> = await getPagedRunEventKeys(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching RunEvent Keys:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };
  
  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setIdToEdit(undefined);
    fetchData(filterReq);
  };
  
  const onEditItem = (id: number)=>{
    setIdToEdit(id);
    handleOpenDrawer();
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={handleOpenDrawer}>
          Add RunEvent Key
        </Button>

        <Stack spacing={6}>
          <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />          
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, onlyMajors: checked == true ? true : undefined}))} >Only Majors</Checkbox>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >      
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Is Major</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: RunEventKey) => (   
            <Checkbox checked={rowData.isMajor} readOnly></Checkbox>
          )}
          </Cell>
        </Column>        
        <Column flexGrow={1} align="left" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (              
              <IconButton onClick={() => onEditItem(rowData.id)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

      <RunEventKeyDrawer 
        RunEventKeyId={idToEdit} 
        open={openDrawer} 
        onClose={handleCloseDrawer} 
      />
    </PageContent>
  );
};

export default RunEventKeyList;
